

import { useRef, useMemo } from "react";
import { FormInput } from "@/components/Inputs/FormInput";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Spacing } from "@/components/Spacing/Spacing";
import { useTranslations, useLocale } from "next-intl";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { IconText } from "@/components/IconText/IconText";
import { Link } from "react-router-dom";
import { useInviteRegisterForm } from "./useInviteRegisterForm";
import {
  getLanguageDisplayNames,
  getLocaleFromDisplayName,
  getDisplayNameFromLocale,
} from "@/lib/utils/languages";
import { Locale } from "@/i18n/config";
import { TermsConsentCheckbox } from "@/components/Legal/TermsConsentCheckbox";

/**
 * The invite form (`/auth/register?invite_code=…`). D11 (AVV plan Rev. 6.5,
 * P6a.4): invitations only — no "I have an invite code" switch and no company
 * block (account-type note, country, tax ID, DPA box) any more. A company is
 * founded through the normal registration, where the AVV is concluded; the
 * form links it.
 */
export default function InviteRegisterForm() {
  const t = useTranslations("register");
  const locale = useLocale();

  const {
    formData,
    formErrors,
    passwordValidation,
    isLoading,
    handleInputChange,
    handleLanguageChange,
    handleSubmit,
    rateLimited,
    termsAccepted,
    termsInvalid,
    handleTermsChange,
    termsRef,
    inviteCodeRef,
  } = useInviteRegisterForm();

  const languageInputRef = useRef<HTMLInputElement>(null);
  const languageOptions = useMemo(
    () => getLanguageDisplayNames(locale),
    [locale],
  );
  const languageDisplayValue = useMemo(() => {
    if (!formData.language) return "";
    return getDisplayNameFromLocale(formData.language as Locale, locale);
  }, [formData.language, locale]);

  return (
    <form onSubmit={handleSubmit} noValidate className="w-full">
      {/* L7-REVAL — `noValidate` so the app's OWN validation is what runs.
          Commit 4366f35a switched this email field text→email. The browser's
          native HTML5 email constraint then blocked the form submit BEFORE
          onSubmit→handleSubmit, so validateEmail never ran and the styled,
          translated "Invalid email address" never rendered — the user saw only
          the browser's native bubble, in the BROWSER's locale, unstyled, and
          positioned by the browser.
          Kept as type="email" rather than reverted to text: the type is what
          gives mobile users the @/. keyboard and the right autofill hint. It
          is the native VALIDATION that had to go, not the input type. Every
          form here renders its own `error=` for the field, so nothing is left
          unvalidated. */}
      {/* D11: no company block — the invitee joins an existing organisation. */}
      {/* Language */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
        <OptionInput
          inputRef={languageInputRef}
          options={languageOptions}
          sortOptions={true}
          defaultValue={languageDisplayValue}
          required={false}
          label={t("appLanguage")}
          testId="auth-register-language"
          setParent={(displayName) => {
            const loc = getLocaleFromDisplayName(displayName, locale);
            if (loc) handleLanguageChange(loc);
          }}
        />
      </div>

      {/* Visual separator between meta info and credentials */}
      <div className="border-t outline-quaternary my-8" />

      {/* Name fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
        {["firstName", "lastName"].map((id) => (
          <FormInput
            autoComplete={true}
            key={id}
            id={id}
            value={formData[id as keyof typeof formData]}
            label={t(id)}
            onChangeAction={(value) =>
              handleInputChange(id as keyof typeof formData, value)
            }
            error={formErrors[id]}
            className="flex-1"
          />
        ))}
      </div>

      {/* Email */}
      <FormInput
        key="email"
        id="email"
        type="email"
        inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
        value={formData.email}
        autoComplete={true}
        label={t("email")}
        onChangeAction={(value) => handleInputChange("email", value)}
        error={formErrors.email}
        className="mt-6"
      />

      {/* Password fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full mt-6">
        {["password", "confirmPassword"].map((id) => (
          <FormInput
            key={id}
            id={id}
            type="password"
            value={formData[id as keyof typeof formData]}
            label={t(id)}
            onChangeAction={(value) =>
              handleInputChange(id as keyof typeof formData, value)
            }
            error={formErrors[id]}
          />
        ))}
      </div>

      <Spacing height="h-4" />

      {/* Password validation */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {["length", "uppercase", "lowercase", "specialChar"].map((id) => (
          <IconText
            key={id}
            isValid={passwordValidation[id as keyof typeof passwordValidation]}
            text={t(id)}
            defaultIcon={<IconCircleX size={16} stroke={2} />}
            successIcon={<IconCircleCheck size={16} stroke={2} />}
          />
        ))}
      </div>

      <Spacing height="h-6" />

      {/* D11: the invite code — always here, required, prefilled from the
          link. No switch to a company lane: an own organisation is founded
          through the normal registration, linked below the field. */}
      <FormInput
        key="inviteCode"
        id="inviteCode"
        ref={inviteCodeRef}
        value={formData.inviteCode || ""}
        label={t("inviteCode")}
        autoComplete={true}
        onChangeAction={(value) => handleInputChange("inviteCode", value)}
        error={formErrors.inviteCode}
        inputProps={{ "aria-required": true }}
      />
      <p className="body-3-regular font-tertiary mt-1">{t("inviteCodeForOrganisationInfo")}</p>
      <Link
        to="/auth/register"
        className="body-3-medium underline mt-1 inline-block"
        data-testid="auth-register-own-organisation"
      >
        {t("ownOrganisationLink")}
      </Link>

      <Spacing height="h-6" />

      {/* Explicit tick (W9 1-S): the terms+privacy sentence. No DPA box — an
          invitee joins an organisation whose DPA already stands (⚑A1).
          Validation on the click: a box left unticked is marked, explained
          and focused. */}
      <TermsConsentCheckbox
        ref={termsRef}
        isChecked={termsAccepted}
        isInvalid={termsInvalid}
        onChangeAction={handleTermsChange}
        testId="auth-register-terms"
      />

      {formErrors.consent && (
        <p
          className="body-2-regular text-error mt-2"
          role="alert"
          data-testid="auth-register-consent-error"
        >
          {formErrors.consent}
        </p>
      )}

      <Spacing height="h-8" />

      {/* Submit */}
      <PrimaryButton
        type="submit"
        text={t("createAccount")}
        size="small"
        disabled={isLoading || rateLimited}
        loading={isLoading}
      />

      <Spacing height="h-3" />

      {/* Login link */}
      <Link to="/auth/login">
        <SecondaryButton text={t("alreadyHaveAccount")} size="small" />
      </Link>
    </form>
  );
}
